import type {
  ChunkOffsetBox, DataEntryUrlBox, DataReferenceBox, EditListBox, FLACSampleEntry, FLACSpecificBox,
  HandlerBox, MediaHeaderBox, MovieFragmentHeaderBox, MovieHeaderBox, Mp4Box, SampleDescriptionBox,
  SampleSizeBox, SampleToChunkBox, SoundMediaHeaderBox, TimeToSampleBox, TrackExtendsBox,
  TrackFragmentBaseMediaDecodeTimeBox, TrackFragmentHeaderBox, TrackHeaderBox,
  TrackRunBox, TrackRunBoxSample,
} from '../mp4/parser';
import { BitWriter } from './bitWriter';
import { SAMPLES_PER_CHANNEL_PER_FRAME, writeFrame, writeStreamInfoMetaDataBlock, type FlacCompressionBufferWrapper } from './flacEncoder';
import { encodeBox } from '../mp4/encoder';
import { concatByteArrays } from '../util/byteArray';
import type { FragmentedMp4Buffers } from '../mp4/fragmentedMp4Types';


export interface FlacData {
  sampleBuffers: Uint8Array[];
  totalSamples: number;
  metadataBlockHeader: Uint8Array;
  samplesPerChannelPerFrame: number;
}

export function getFlacData(
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,
  fragmentedMp4: boolean,
): FlacData {
  // This array will contain one buffer per FLAC frame, i.e. one buffer
  // sample to describe in mp4
  const sampleBuffers: Uint8Array[] = [];

  const bufferWrapper: FlacCompressionBufferWrapper = {};

  const frameSize = nChannels * SAMPLES_PER_CHANNEL_PER_FRAME * 2;
  for (let nFrame = 0; nFrame * frameSize < pcm_s16le_bytes.length; nFrame++) {
    const frameBytes = pcm_s16le_bytes.slice(nFrame * frameSize, (nFrame + 1) * frameSize);
    sampleBuffers.push(writeFrame(bufferWrapper, nFrame, frameBytes, nChannels, sampleRateInHz, compressed));
  }

  const totalSamples = pcm_s16le_bytes.length / (nChannels * 2);
  const metadataBlockHeader = writeStreamInfoMetaDataBlock(nChannels, sampleRateInHz, fragmentedMp4 ? 0 : totalSamples);

  return {
    sampleBuffers,
    totalSamples,
    metadataBlockHeader,
    samplesPerChannelPerFrame: SAMPLES_PER_CHANNEL_PER_FRAME,
  };
}



/**
 * Given some raw PCM samples, returns a buffer containing a valid
 * .mp4 file where the audio data is a flac stream containing these
 * uncompressed PCM samples.
 *
 * FLAC in MP4 mapping: https://github.com/xiph/flac/blob/master/doc/isoflac.txt
 */
export function encodeToFlacInMp4(
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,

  // If true, the output will include extra boxes like 'free' and 'edts' to make the
  // output easier to compare with the output from ffmpeg
  debug = false,
): Uint8Array {

  const { sampleBuffers, totalSamples, metadataBlockHeader } = getFlacData(pcm_s16le_bytes, nChannels, sampleRateInHz, compressed, false);

  const ftypBuffer = writeFtyp(false);
  const freeBuffer = debug ? writeFree() : new Uint8Array(0);
  const mdatBuffer = writeMdat(sampleBuffers);

  const moovBuffer = writeMoov(false, debug, sampleBuffers, ftypBuffer.length + freeBuffer.length + 8, metadataBlockHeader, sampleRateInHz, totalSamples, nChannels);

  return concatByteArrays([ftypBuffer, freeBuffer, mdatBuffer, moovBuffer]);
}


/**
 * Given some raw PCM samples, returns a buffer containing a valid
 * fragmented .mp4 file where the audio data is a flac stream containing these
 * uncompressed PCM samples.
 *
 * FLAC in MP4 mapping: https://github.com/xiph/flac/blob/master/doc/isoflac.txt
 */
export function encodeToFlacInFragmentedMp4(
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,
  debug = false,
): Uint8Array {
  const fragmentedMp4Buffers: FragmentedMp4Buffers = encodeToFlacInFragmentedMp4Buffers(0, pcm_s16le_bytes, nChannels, sampleRateInHz, compressed, debug);
  return concatByteArrays([fragmentedMp4Buffers.initialization!, fragmentedMp4Buffers.mediaSegment]);
}


export function encodeToFlacInFragmentedMp4Buffers(
  baseMediaDecodeTime: number,
  pcm_s16le_bytes: Uint8Array,
  nChannels: number,
  sampleRateInHz: number,
  compressed: boolean,
  debug = false,
): FragmentedMp4Buffers {

  const { sampleBuffers, totalSamples, metadataBlockHeader } = getFlacData(pcm_s16le_bytes, nChannels, sampleRateInHz, compressed, true);

  const ftypBuffer = writeFtyp(true);
  const mdatBuffer = writeMdat(sampleBuffers);

  const moovBuffer = writeMoov(true, debug, sampleBuffers, ftypBuffer.length + 8, metadataBlockHeader, sampleRateInHz, totalSamples, nChannels);

  const moofBuffer = writeMoof(sampleBuffers, baseMediaDecodeTime);

  const initialization = concatByteArrays([ftypBuffer, moovBuffer]);
  const mediaSegment = concatByteArrays([moofBuffer, mdatBuffer]);
  return {
    initialization,
    mediaSegment,
    info: {},
  };
}


function writeMoof(sampleBuffers: Uint8Array[], baseMediaDecodeTime: number): Uint8Array {
  const moofBox = makeMoofBox(sampleBuffers, baseMediaDecodeTime);
  return encodeBox(moofBox);
}


function makeMoofBox(sampleBuffers: Uint8Array[], baseMediaDecodeTime: number): Mp4Box {

  const mfhd: MovieFragmentHeaderBox = {
    type: 'mfhd',
    sequence_number: 1,
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: []
  };

  const tfhd: TrackFragmentHeaderBox = {
    type: 'tfhd',
    track_ID: 1,
    version: 0,
    flags: 58,
    size: 0,
    start: 0,
    children: [],
    base_data_offset: 0,
    sample_description_index: 1,
    default_sample_duration: 0,
    default_sample_size: 0,
    default_sample_flags: 0,
  };

  const tfdt: TrackFragmentBaseMediaDecodeTimeBox = {
    type: 'tfdt',
    baseMediaDecodeTime,
    version: 1,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const trun: TrackRunBox = {
    type: 'trun',
    sample_count: sampleBuffers.length,
    samples: sampleBuffers.map<TrackRunBoxSample>(b => ({
      // The flac code has been refactored to enforce a sample duration of 1024 samples,
      // to match AAC frames
      sample_duration: 1024,
      sample_size: b.byteLength,
    })),
    version: 0,
    flags: 769,
    size: 0,
    start: 0,
    children: [],

    // This value will be updated below once we know the size of the moof box
    data_offset: 0,
  };

  const traf: Mp4Box = {
    type: 'traf',
    size: 0,
    start: 0,
    children: [tfhd, tfdt, trun],
  };

  const moof: Mp4Box = {
    type: 'moof',
    size: 0,
    start: 0,
    children: [mfhd, traf],
  };

  // The data_offset properties in the trun box is the offset to add to the property
  // base_data_offset of the tfhd box in order to get to the beginning of the data in
  // the mdat box. We calculate it by measure the moof box and adding 8 for the size and type
  // properties of the mdat box
  const buffer = encodeBox(moof);
  trun.data_offset = buffer.byteLength + 8;

  return moof;
}


function writeMoov(produceFragmentedMp4: boolean, debug: boolean, sampleBuffers: Uint8Array[], dataOffset: number, metadataBlockHeader: Uint8Array, sampleRateInHz: number, nSamples: number, nChannels: number): Uint8Array {
  const moovBox = makeMoovBox(produceFragmentedMp4, debug, sampleBuffers, dataOffset, metadataBlockHeader, sampleRateInHz, nSamples, nChannels);
  return encodeBox(moovBox);
}

export function makeFragmentedMp4MoovBox(debug: boolean, metadataBlockHeader: Uint8Array, sampleRateInHz: number, nChannels: number): Mp4Box {
  return makeMoovBox(true, debug, [], NaN, metadataBlockHeader, sampleRateInHz, NaN, nChannels);
}

function makeMoovBox(produceFragmentedMp4: boolean, debug: boolean, sampleBuffers: Uint8Array[], dataOffset: number, metadataBlockHeader: Uint8Array, sampleRateInHz: number, nSamples: number, nChannels: number): Mp4Box {
  const mvhd: MovieHeaderBox = {
    type: 'mvhd',
    creation_time: 0,
    modification_time: 0,
    timescale: sampleRateInHz,
    duration: produceFragmentedMp4 ? 0xFFFFFFFF : nSamples,
    rate: 0x00010000,
    volume: 0x0100,
    matrix: [0x00010000, 0, 0, 0, 0x00010000, 0, 0, 0, 0x40000000],
    next_track_id: 0xFFFFFFFF,
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const tkhd: TrackHeaderBox = {
    type: 'tkhd',
    start: 0,
    size: 0,
    children: [],
    creation_time: 0,
    modification_time: 0,
    track_id: 1,
    duration: produceFragmentedMp4 ? 0xFFFFFFFF : nSamples,
    layer: 0,
    alternate_group: 0,
    volume: 0x0100,
    matrix: [0x00010000, 0, 0, 0, 0x00010000, 0, 0, 0, 0x40000000],
    width: 0,
    height: 0,
    version: 0,
    flags: produceFragmentedMp4 ? 7 : 3,
  };

  const elst: EditListBox = {
    type: 'elst',
    size: 0,
    start: 0,
    children: [],
    entries: [
      {
        segment_duration: nSamples,
        media_time: 0,
        media_rate_integer: 1,
        media_rate_fraction: 0,
      },
    ],
    version: 0,
    flags: 0
  };

  const edts: Mp4Box = {
    type: 'edts',
    size: 0,
    start: 0,
    children: [elst],
  };

  const mdhd: MediaHeaderBox = {
    type: 'mdhd',
    start: 0,
    size: 0,
    children: [],
    creation_time: 0,
    modification_time: 0,
    timescale: sampleRateInHz,
    duration: produceFragmentedMp4 ? 0xFFFFFFFF : nSamples,
    language: 21956,
    languageString: 'und',
    version: 0,
    flags: 0,
  };

  const hdlr: HandlerBox = {
    type: 'hdlr',
    handler_type: 'soun',
    name: 'SoundHandler',
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const smhd: SoundMediaHeaderBox = {
    type: 'smhd',
    balance: 0,
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const url: DataEntryUrlBox = {
    type: 'url ',
    location: '',
    version: 0,
    flags: 1,
    size: 0,
    start: 0,
    children: [],
  };

  const dref: DataReferenceBox = {
    type: 'dref',
    size: 0,
    start: 0,
    children: [url],
    version: 0,
    flags: 0
  };

  const dinf: Mp4Box = {
    type: 'dinf',
    size: 0,
    start: 0,
    children: [dref],
  };

  const fLaC = makeFlacBox(nChannels, sampleRateInHz, metadataBlockHeader);

  const stsd: SampleDescriptionBox = {
    type: 'stsd',
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [fLaC],
  };

  const stts: TimeToSampleBox = {
    type: 'stts',
    sample_count: produceFragmentedMp4 ? [] : [nSamples],
    sample_delta: produceFragmentedMp4 ? [] : [sampleRateInHz],
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const stsc: SampleToChunkBox = {
    type: 'stsc',
    first_chunk: produceFragmentedMp4 ? [] : [1],
    samples_per_chunk: produceFragmentedMp4 ? [] : [sampleBuffers.length],
    sample_description_index: produceFragmentedMp4 ? [] : [1],
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const stsz: SampleSizeBox = {
    type: 'stsz',
    sample_size: 0,
    sample_count: produceFragmentedMp4 ? 0 : sampleBuffers.length,
    sample_sizes: produceFragmentedMp4 ? [] : sampleBuffers.map(b => b.byteLength),
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const stco: ChunkOffsetBox = {
    type: 'stco',
    chunk_offsets: produceFragmentedMp4 ? [] : [dataOffset],
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const stbl: Mp4Box = {
    type: 'stbl',
    size: 0,
    start: 0,
    children: [stsd, stts, stsc, stsz, stco],
  };

  const minf: Mp4Box = {
    type: 'minf',
    start: 0,
    size: 0,
    children: [smhd, dinf, stbl],
  };

  const mdia: Mp4Box = {
    type: 'mdia',
    start: 0,
    size: 0,
    children: [mdhd, hdlr, minf],
  };


  const trakChildren: Mp4Box[] = [tkhd];
  if (debug && !produceFragmentedMp4) {
    trakChildren.push(edts);
  }
  trakChildren.push(mdia);
  const trak: Mp4Box = {
    type: 'trak',
    start: 0,
    size: 0,
    children: trakChildren,
  };

  const moov: Mp4Box = {
    type: 'moov',
    start: 0,
    size: 0,
    children: [mvhd, trak],
  };

  if (!produceFragmentedMp4) {
    return moov;
  }

  // Below are boxes specific to fragmented mp4

  const trex: TrackExtendsBox = {
    type: 'trex',
    track_ID: 1,
    default_sample_description_index: 1,
    default_sample_duration: 0,
    default_sample_size: 0,
    default_sample_flags: 65536,
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: []
  };


  const mvex: Mp4Box = {
    type: 'mvex',
    start: 0,
    size: 0,
    children: [trex],
  };

  moov.children.push(mvex);

  if (debug) {
    // The moov box ends with a udta box that contains metadata
    // so let's add an equivalent padding box to preserve offsets
    // of things that will come after the moov box
    const pseudoUdta: Mp4Box = {
      type: 'free',
      start: 0,
      size: 97,
      children: [],
    };
    moov.children.push(pseudoUdta);
  }

  return moov;
}


export function makeFlacBox(nChannels: number, sampleRateInHz: number, metadataBlockHeader: Uint8Array): FLACSampleEntry {
  const dfLa: FLACSpecificBox = {
    type: 'dfLa',
    flacMetadataBlocks: [{
      LastMetadataBlockFlag: true,
      BlockType: 0,
      BlockData: [...metadataBlockHeader],
    }],
    version: 0,
    flags: 0,
    size: 0,
    start: 0,
    children: [],
  };

  const fLaC: FLACSampleEntry = {
    type: 'fLaC',
    channelcount: nChannels,
    samplesize: 16,
    samplerate: sampleRateInHz,
    data_reference_index: 1,
    size: 0,
    start: 0,
    children: [dfLa],
  };
  return fLaC;
}


function writeMdat(sampleBuffers: Uint8Array[]): Uint8Array {
  const bitWriter = new BitWriter();

  let size = 8;
  for (const b of sampleBuffers) {
    size += b.byteLength;
  }

  bitWriter.writeBits(size, 32);
  bitWriter.writeString('mdat');

  const mdatHeader = bitWriter.getBuffer();
  return concatByteArrays([mdatHeader, ...sampleBuffers]);
}


function writeFtyp(addIso6: boolean): Uint8Array {
  const bitWriter = new BitWriter();

  const size = addIso6 ? 32 : 28;

  bitWriter.writeBits(size, 32);
  bitWriter.writeString('ftyp');
  bitWriter.writeString('isom');
  bitWriter.writeBits(512, 32);
  bitWriter.writeString('isom');
  if (addIso6) {
    bitWriter.writeString('iso6');
  }
  bitWriter.writeString('iso2');
  bitWriter.writeString('mp41');

  return bitWriter.getBuffer();
}


function writeFree(): Uint8Array {
  const bitWriter = new BitWriter();

  const size = 8;

  bitWriter.writeBits(size, 32);
  bitWriter.writeString('free');

  return bitWriter.getBuffer();
}
